import React, { useState } from 'react';
import {
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import { useToast } from '../../contexts/ToastContext';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { STAFF_ROLE_TYPES as STAFF_ROLES } from '../../constants';
import type { SchoolsResponse } from '../../types';

export default function StaffRolesTab() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { canEdit } = useFeatureAccess('staffRoles');
  const [roles, setRoles] = useState<{ value: string; label: string }[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [roleValue, setRoleValue] = useState('');
  const [roleLabel, setRoleLabel] = useState('');

  const { data: schoolsData } = useQuery<SchoolsResponse>('schools', async () => {
    const res = await apiClient.get('/schools');
    return res.data as SchoolsResponse;
  });
  const school = schoolsData?.schools?.[0];

  React.useEffect(() => {
    const list = school?.settings?.staffRoles ?? [];
    setRoles(Array.isArray(list) ? list.map((r) => ({ value: r.value || '', label: r.label ?? r.value ?? '' })) : []);
  }, [school?.settings?.staffRoles]);

  const saveMutation = useMutation(
    async (payload: { _id: string; settings: Record<string, unknown> }) => {
      const res = await apiClient.put(`/schools/${payload._id}`, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('schools');
        showToast('success', 'Saved.');
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to save');
      },
    }
  );

  const handleSave = (newList: { value: string; label: string }[]) => {
    if (!school) return;
    setRoles(newList);
    saveMutation.mutate({
      _id: school._id,
      settings: { ...school.settings, staffRoles: newList },
    });
  };

  const availableToAdd = STAFF_ROLES.filter((r) => !roles.some((x) => x.value === r.value));

  const handleAdd = () => {
    const first = availableToAdd[0];
    setRoleValue(first?.value ?? '');
    setRoleLabel(first?.label ?? '');
    setAddOpen(true);
  };

  const handleAddSubmit = () => {
    const value = roleValue.trim();
    const label = roleLabel.trim() || value;
    if (!value) {
      showToast('error', 'Role is required');
      return;
    }
    if (roles.some((r) => r.value === value)) {
      showToast('error', 'This role is already in the list');
      return;
    }
    handleSave([...roles, { value, label }]);
    setAddOpen(false);
  };

  const handleEdit = (idx: number) => {
    setEditingIndex(idx);
    const r = roles[idx];
    setRoleValue(r?.value ?? '');
    setRoleLabel(r?.label ?? '');
    setEditOpen(true);
  };

  const handleEditSubmit = () => {
    if (editingIndex < 0 || editingIndex >= roles.length) return;
    const label = (roleLabel.trim() || roles[editingIndex]?.value) ?? '';
    if (!label) {
      showToast('error', 'Label is required');
      return;
    }
    const newList = roles.map((r, i) => (i === editingIndex ? { ...r, label } : r));
    handleSave(newList);
    setEditOpen(false);
    setEditingIndex(-1);
  };

  const handleDelete = (idx: number) => {
    if (!window.confirm(`Remove role "${roles[idx]?.label || roles[idx]?.value}" from the list?`)) return;
    handleSave(roles.filter((_, i) => i !== idx));
  };

  return (
    <Box>
      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} disabled={availableToAdd.length === 0}>
            Add role
          </Button>
        </Box>
      )}
      {roles.length === 0 ? (
        <Paper sx={{ py: 4, px: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No staff roles defined. Add roles here; they will appear in the Role dropdown when adding or editing staff. If you leave this empty, default roles (Teacher, Bus Driver, etc.) are used.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Role</TableCell>
                {canEdit && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((r, idx) => (
                <TableRow key={r.value}>
                  <TableCell>{r.label}</TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <IconButton size="small" title="Edit" onClick={() => handleEdit(idx)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" title="Delete" color="error" onClick={() => handleDelete(idx)} disabled={saveMutation.isLoading}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add staff role</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={roleValue}
              label="Role"
              onChange={(e) => {
                const v = e.target.value as string;
                setRoleValue(v);
                setRoleLabel(STAFF_ROLES.find((r) => r.value === v)?.label ?? v);
              }}
            >
              {availableToAdd.map((r) => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField fullWidth label="Label" value={roleLabel} onChange={(e) => setRoleLabel(e.target.value)} margin="normal" placeholder="Display name (e.g. Teacher)" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          {canEdit && (
            <Button variant="contained" onClick={handleAddSubmit} disabled={saveMutation.isLoading}>Add</Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={editOpen} onClose={() => { setEditOpen(false); setEditingIndex(-1); }} maxWidth="xs" fullWidth>
        <DialogTitle>Edit staff role</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Label" value={roleLabel} onChange={(e) => setRoleLabel(e.target.value)} margin="normal" placeholder="Display name" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditOpen(false); setEditingIndex(-1); }}>Cancel</Button>
          {canEdit && (
            <Button variant="contained" onClick={handleEditSubmit} disabled={saveMutation.isLoading}>Save</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
